import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import Supervision from "@/models/Supervision";
import SupervisionHistory from "@/models/SupervisionHistory";
import {
  supervisionApi,
  supervisionHistoryApi,
  supervisionItemsApi,
  supervisionChaptersApi,
  supervisionCommentsApi,
} from "@/api/supervisionClient";
import { snakeToCamel } from "@/utils/dataShape";

const SupervisionRepo = useRepo(Supervision);
const SupervisionHistoryRepo = useRepo(SupervisionHistory);

export const useSupervisionStore = defineStore("supervision", {
  state: () => ({
    currentSupervisionUuid: null,
    error: null,
    showSupervisionPanelManually: false,
  }),

  getters: {
    currentSupervision: (state) => {
      if (!state.currentSupervisionUuid) return null;
      return SupervisionRepo.find(state.currentSupervisionUuid);
    },

    supervisionHistory: (state) => {
      if (!state.currentSupervisionUuid) return [];
      return SupervisionHistoryRepo.where("supervisionUuid", state.currentSupervisionUuid).get();
    },

    supervisionHistoryByProject: () => (projectUuid) => {
      const supervision = SupervisionRepo.where("projectUuid", projectUuid).first();

      if (!supervision) return [];

      return SupervisionHistoryRepo.where("supervisionUuid", supervision.uuid).get();
    },
  },

  actions: {
    setShowSupervisionPanel(value) {
      this.showSupervisionPanelManually = value;
    },
    async fetchSupervisionByProject(projectUuid) {
      this.error = null;

      try {
        const result = await supervisionApi.list({ project: projectUuid });
        const supervision = result.results?.[0] || null;

        if (!supervision) {
          this.currentSupervisionUuid = null;
          return null;
        }

        const supervisionData = {
          ...supervision,
          projectUuid,
        };

        SupervisionRepo.save(supervisionData);
        this.currentSupervisionUuid = supervisionData.uuid;

        return supervisionData;
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching supervision:", error);
        throw error;
      }
    },

    async fetchSupervisionHistory(supervisionUuid) {
      this.error = null;

      try {
        SupervisionHistoryRepo.where("supervisionUuid", supervisionUuid).delete();
        let page = 1;
        let hasNextPage = true;
        while (hasNextPage) {
          const result = await supervisionHistoryApi.list({
            supervisionUuid,
            page,
            page_size: 100,
          });
          const historyData = (result.results || result).map((item) => ({
            ...item,
            supervisionUuid,
          }));

          SupervisionHistoryRepo.save(historyData);
          hasNextPage = !!result.next;
          page++;
        }
        return SupervisionHistoryRepo.where("supervisionUuid", supervisionUuid).get();
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching supervision history:", error);
        throw error;
      }
    },
    async fetchSupervisionAllData(projectUuid) {
      const supervision = await this.fetchSupervisionByProject(projectUuid);
      try {
        const supervisionAllData = await supervisionApi.retrieve({
          uuid: supervision.uuid,
        });
        const transformedData = snakeToCamel({ ...supervisionAllData }, "flatten");
        return transformedData;
      } catch (error) {
        this.error = error.message;
        console.error("Error fetching supervision data:", error);
        throw error;
      }
    },

    async startSupervision(data) {
      this.error = null;

      try {
        const result = await supervisionApi.startSupervision({
          supervisionStartRequest: data,
        });

        await this.fetchSupervisionHistory(result.supervision);

        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error starting supervision:", error);
        throw error;
      }
    },

    async endSupervision(supervisionUuid, data) {
      this.error = null;

      try {
        const result = await supervisionApi.endSupervision({
          uuid: supervisionUuid,
          supervisionEndRequest: data,
        });

        await this.fetchSupervisionHistory(supervisionUuid);

        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error ending supervision:", error);
        throw error;
      }
    },

    async checkActiveSupervision(projectUuid) {
      try {
        const result = await supervisionApi.checkActiveSupervision({
          projectUuid: projectUuid,
        });
        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error checking active supervision:", error);
      }
    },

    async updateSupervisionItemValidation(data) {
      this.error = null;

      try {
        const result = await supervisionItemsApi.partialUpdate_8({
          supervisionUuid: data.supervisionUuid,
          supervisionChapterUuid: data.supervisionChapterUuid,
          uuid: data.supervisionItemUuid,
          patchedSupervisionItemRequest: {
            supervisionChapter: data.supervisionChapterUuid,
            item: data.supervisionItemUuid,
            status: data.validationStatus,
          },
        });

        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error updating supervision item validation:", error);
        throw error;
      }
    },

    async updateSupervisionChapterValidation(data) {
      this.error = null;

      try {
        const result = await supervisionChaptersApi.partialUpdate_4({
          supervisionUuid: data.supervisionUuid,
          uuid: data.supervisionChapterUuid,
          patchedSupervisionChapterRequest: {
            supervision: data.supervisionUuid,
            chapter: data.supervisionChapterUuid,
            status: true,
          },
        });

        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error updating supervision chapter validation:", error);
        throw error;
      }
    },

    async resetSupervisionChapter(data) {
      this.error = null;

      try {
        const result = await supervisionChaptersApi.resetChapter({
          supervisionUuid: data.supervisionUuid,
          uuid: data.supervisionChapterUuid,
          supervisionChapterRequest: {
            supervision: data.supervisionUuid,
          },
        });

        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error resetting supervision chapter:", error);
        throw error;
      }
    },

    async addComment(data) {
      this.error = null;
      try {
        const result = await supervisionCommentsApi.create({
          supervisionUuid: data.supervisionUuid,
          supervisionCommentRequest: {
            supervision: data.supervisionUuid,
            supervisionChapter: data.supervisionChapterUuid,
            authorUuid: data.authorUuid,
            text: data.comment,
            isResolved: false,
          },
        });
        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error adding comment:", error);
        throw error;
      }
    },

    async markCommentAsProcessed(supervisionUuid, commentUuid) {
      this.error = null;
      try {
        const result = await supervisionCommentsApi.partialUpdate({
          supervisionUuid,
          uuid: commentUuid,
          patchedSupervisionCommentRequest: {
            isResolved: true,
            resolvedAt: new Date(),
          },
        });
        return result;
      } catch (error) {
        this.error = error.message;
        console.error("Error marking comment as processed:", error);
        throw error;
      }
    },

    async deleteComment(supervisionUuid, commentUuid) {
      this.error = null;
      try {
        await supervisionCommentsApi.destroy({
          supervisionUuid,
          uuid: commentUuid,
        });
        return { success: true };
      } catch (error) {
        this.error = error.message;
        console.error("Error deleting comment:", error);
        throw error;
      }
    },
  },
});
